import { createFileRoute } from '@tanstack/react-router';
import { StatCard } from '@/components/ui/StatCard';
import { StatusBadge } from '@/components/ui/StatusBadge';
import type { Icon } from '@phosphor-icons/react';
import {
  CheckCircle,
  LinkBreak,
  Clock,
  Fingerprint,
  IdentificationCard,
  At,
} from '@phosphor-icons/react';

type IconType = Icon;

const STRATEGIES: { name: string; rate: number; icon: IconType; description: string }[] = [
  { name: 'Gateway Txn ID Match', rate: 98.1, icon: Fingerprint, description: 'Match disputes to transactions via gateway transaction ID.' },
  { name: 'Gateway Customer ID', rate: 87.4, icon: IdentificationCard, description: 'Link records through shared gateway customer references.' },
  { name: 'Email Match', rate: 72.8, icon: At, description: 'Fuzzy match billing and PSP records by normalized email.' },
];

const RECENT_RUNS = [
  { id: '#RC-4021', type: 'Full Reconciliation', matched: '14,291', unmatched: '847', status: 'success' as const, time: '2h ago' },
  { id: '#RC-4020', type: 'Incremental Sync', matched: '3,102', unmatched: '62', status: 'success' as const, time: '6h ago' },
  { id: '#RC-4019', type: 'Full Reconciliation', matched: '14,088', unmatched: '912', status: 'failed' as const, time: '10h ago' },
];

function SyncPage() {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Sync & Reconciliation</h2>
        <p className="text-slate-500 text-sm mt-1">
          Cross-platform transaction matching and data reconciliation across billing and PSP sources.
        </p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-3 gap-4">
        <StatCard label="Match Rate" value="94.2%" icon={CheckCircle} iconColor="text-green-600" change="+1.3%" />
        <StatCard label="Unmatched Records" value={847} icon={LinkBreak} iconColor="text-amber-500" change="-8%" changeColor="text-green-500" />
        <StatCard label="Last Reconciliation" value="2h ago" icon={Clock} iconColor="text-blue-600" />
      </div>

      {/* Reconciliation Strategy Cards */}
      <div>
        <h3 className="text-lg font-bold text-slate-900 mb-3">Reconciliation Strategies</h3>
        <div className="grid grid-cols-3 gap-4">
          {STRATEGIES.map((s) => {
            const Icon = s.icon;
            return (
            <div key={s.name} className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Icon size={18} weight="regular" className="text-primary" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-slate-900">{s.name}</h4>
                  <p className="text-xs text-slate-400 mt-0.5">{s.description}</p>
                </div>
              </div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-slate-400 font-medium">Match Rate</span>
                <span className="text-sm font-bold text-slate-900">{s.rate}%</span>
              </div>
              <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{ width: `${s.rate}%` }}
                />
              </div>
            </div>
            );
          })}
        </div>
      </div>

      {/* Recent Reconciliation Runs */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <h3 className="text-lg font-bold text-slate-900">Recent Reconciliation Runs</h3>
        </div>
        <table className="w-full">
          <thead>
            <tr className="text-xs text-slate-400 font-medium border-b border-slate-100 bg-slate-50/50">
              <th className="text-left px-6 py-3">Run ID</th>
              <th className="text-left px-6 py-3">Type</th>
              <th className="text-right px-6 py-3">Matched</th>
              <th className="text-right px-6 py-3">Unmatched</th>
              <th className="text-left px-6 py-3">Status</th>
              <th className="text-right px-6 py-3">Time</th>
            </tr>
          </thead>
          <tbody>
            {RECENT_RUNS.map((run) => (
              <tr key={run.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 transition">
                <td className="px-6 py-4 text-sm font-mono text-primary font-medium">{run.id}</td>
                <td className="px-6 py-4 text-sm text-slate-600">{run.type}</td>
                <td className="px-6 py-4 text-sm text-slate-700 text-right font-medium">{run.matched}</td>
                <td className="px-6 py-4 text-sm text-slate-700 text-right font-medium">{run.unmatched}</td>
                <td className="px-6 py-4">
                  <StatusBadge variant={run.status} />
                </td>
                <td className="px-6 py-4 text-sm text-slate-500 text-right">{run.time}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/pipeline/sync')({
  component: SyncPage,
});
